'use client';

import { useEffect, useState } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  origin: string;
  destination: string;
  originName: string;
  destinationName: string;
  className?: string;
  onAdded?: () => void;
}

export function FavoriteButton({
  origin,
  destination,
  originName,
  destinationName,
  className,
  onAdded,
}: FavoriteButtonProps) {
  const { data: session, status } = useSession();
  const [favoriteId, setFavoriteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') {
      setChecked(true);
      return;
    }

    let cancelled = false;
    fetch('/api/favorites')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.favorites) return;
        const existing = data.favorites.find(
          (f: any) =>
            f.type === 'flight_route' &&
            f.origin === origin.toUpperCase() &&
            f.destination === destination.toUpperCase()
        );
        setFavoriteId(existing?.id ?? null);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [status, origin, destination]);

  const handleClick = async () => {
    if (status !== 'authenticated') {
      signIn('google');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (favoriteId) {
        const res = await fetch(`/api/favorites?id=${favoriteId}`, { method: 'DELETE' });
        if (res.ok) {
          setFavoriteId(null);
        } else {
          const data = await res.json().catch(() => null);
          setError(data?.error || 'Favoriden kaldırılamadı');
        }
      } else {
        const res = await fetch('/api/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'flight_route',
            origin,
            destination,
            name: `${originName} → ${destinationName}`,
            url: typeof window !== 'undefined' ? window.location.pathname : undefined,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setFavoriteId(data.favorite?.id ?? null);
          onAdded?.();
        } else {
          const data = await res.json().catch(() => null);
          if (res.status === 401) {
            setError('Oturumunuz güncel değil — çıkış yapıp tekrar giriş yapın');
          } else {
            setError(data?.error || 'Bir hata oluştu, tekrar deneyin');
          }
        }
      }
    } catch {
      setError('Bağlantı hatası, tekrar deneyin');
    } finally {
      setLoading(false);
    }
  };

  const isFavorited = !!favoriteId;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <Button
        type="button"
        variant={isFavorited ? 'default' : 'outline'}
        size="sm"
        onClick={handleClick}
        disabled={loading || !checked}
        className={cn('gap-2', className)}
      >
        <Heart className={cn('w-4 h-4', isFavorited && 'fill-current')} />
        {isFavorited ? 'Takip Ediliyor' : 'Bu Rotayı Takip Et'}
      </Button>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
